// Finance Books — Projects & Budgets (Pro)
// • Project owns reusable Execution Budgets (1 project ↔ many exec budgets, each exec
//   budget shared across multiple SOs of that project).
// • Overhead budgets live as a GLOBAL pool (no project). SO allocation parks the
//   overhead portion into a selected global pool.
// • Per-budget Pro Detail view: KPIs (Allocated/Receivables/Received/Payables/Paid/
//   Available/Gate/P&L) + linked transaction tables (SOs/Invoices/Receipts/POs/Bills
//   /Vendor Payments).
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import fb from '../api';
import {
  PageHeader, Btn, Drawer, Input, Select, Textarea, Table, Pill, Kpi,
  fmtMoney, fmtDate, SOFT,
} from '../ui';

// ────────────────────────────────────────────────────────────────────────────
// PROJECTS LIST
// ────────────────────────────────────────────────────────────────────────────
export function Projects() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dash, setDash] = useState(null);
  const [detail, setDetail] = useState(null);
  const [bid, setBid] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ gate_pct: 40 });

  const reload = async () => {
    setRows(await fb.get('/projects'));
    setDash(await fb.get('/projects-dashboard'));
    setCustomers(await fb.get('/customers'));
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      if (!form.name) return toast.error('Name required');
      const payload = { ...form, gate_pct: Number(form.gate_pct || 40) };
      if (edit) await fb.put(`/projects/${edit.id}`, payload);
      else await fb.post('/projects', payload);
      toast.success('Saved'); setOpen(false); setEdit(null); setForm({ gate_pct: 40 }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const openDetail = async (p) => setDetail(await fb.get(`/projects/${p.id}`));

  return (
    <div data-testid="fb-projects">
      <PageHeader title="Projects" subtitle="Value Accumulation · Reusable Budgets · 40% Receipt Gates"
        right={<Btn variant="accent" onClick={() => { setEdit(null); setForm({ gate_pct: 40 }); setOpen(true); }} data-testid="fb-new-project">+ New Project</Btn>} />

      {dash && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Kpi label="Active Projects" value={dash.active_projects} sub={`${dash.projects} total · ${dash.linked_sos} SOs linked`} testid="kpi-projects" />
          <Kpi label="Total SO Value" value={fmtMoney(dash.total_so_value)} sub={`${dash.received_pct}% collected`} tone="blue" testid="kpi-so-value" />
          <Kpi label="Gate Status" value={`${dash.gate_passed_sos} unlocked`} sub={`${dash.gate_pending_sos} pending`} tone={dash.gate_passed_sos ? 'green' : 'orange'} testid="kpi-gate" />
          <Kpi label="Available Budget" value={fmtMoney(dash.total_available)} sub={`Spent ${fmtMoney(dash.total_spent)} · Committed ${fmtMoney(dash.total_committed)}`} tone="green" testid="kpi-available" />
        </div>
      )}

      <Table
        columns={[
          { key: 'code', label: 'Code', render: (r) => <span className="font-mono text-xs text-white/55">{r.code}</span> },
          { key: 'name', label: 'Project', render: (r) => <button onClick={() => openDetail(r)} className="text-white font-medium underline-offset-2 hover:underline" data-testid={`project-open-${r.code}`}>{r.name}</button> },
          { key: 'customer_name', label: 'Customer', render: (r) => r.customer_name || <span className="text-white/30">—</span> },
          { key: 'manager_name', label: 'Manager' },
          { key: 'linked_sos', label: 'SOs', align: 'right' },
          { key: 'total_allocated', label: 'Allocated', align: 'right', render: (r) => fmtMoney(r.total_allocated) },
          { key: 'total_received', label: 'Received', align: 'right', render: (r) => <span className="text-emerald-300">{fmtMoney(r.total_received)}</span> },
          { key: 'total_spent', label: 'Spent', align: 'right', render: (r) => <span className="text-rose-300">{fmtMoney(r.total_spent)}</span> },
          { key: 'status', label: 'Status', render: (r) => <Pill tone={r.status === 'active' ? 'green' : 'grey'}>{r.status}</Pill> },
        ]}
        rows={rows} empty="No projects yet. Click New Project to create one."
        testidPrefix="project"
      />

      <Drawer open={open} onClose={() => setOpen(false)} title={edit ? `Edit ${edit.name}` : 'New Project'} width="max-w-2xl">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name *" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="proj-name" />
          <Input label="Code" value={form.code || ''} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="auto" />
          <Select label="Customer" value={form.customer_id || ''} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
            <option value="">—</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Input label="Project Manager" value={form.manager_name || ''} onChange={(e) => setForm({ ...form, manager_name: e.target.value })} />
          <Input label="Start Date" type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          <Input label="End Date" type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          <Input label="Receipt Gate % (unlocks budgets)" type="number" value={form.gate_pct} onChange={(e) => setForm({ ...form, gate_pct: e.target.value })} data-testid="proj-gate" />
        </div>
        <Textarea label="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={save} data-testid="proj-save">Save Project</Btn>
        </div>
      </Drawer>

      <ProjectDetail
        detail={detail} onClose={() => setDetail(null)}
        onChange={() => { reload(); if (detail) openDetail(detail); }}
        onOpenBudget={(b) => setBid(b.id)}
      />
      <BudgetDetailDrawer bid={bid} onClose={() => setBid(null)} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// PROJECT DETAIL
// ────────────────────────────────────────────────────────────────────────────
function ProjectDetail({ detail, onClose, onChange, onOpenBudget }) {
  const [openAlloc, setOpenAlloc] = useState(false);
  if (!detail) return null;
  const totalAllocated = (detail.budgets || []).reduce((s, b) => s + Number(b.allocated || 0), 0);
  const totalReceived = (detail.budgets || []).reduce((s, b) => s + Number(b.received || 0), 0);
  const totalSpent = (detail.budgets || []).reduce((s, b) => s + Number(b.spent || 0), 0);

  return (
    <Drawer open={!!detail} onClose={onClose} title={`Project · ${detail.name}`} width="max-w-6xl">
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Kpi label="Allocated" value={fmtMoney(totalAllocated)} />
        <Kpi label="Received" value={fmtMoney(totalReceived)} tone="green" />
        <Kpi label="Spent" value={fmtMoney(totalSpent)} tone="red" />
        <Kpi label="Margin" value={fmtMoney(totalReceived - totalSpent)} tone={totalReceived - totalSpent >= 0 ? 'green' : 'red'} />
      </div>

      <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-2">Budgets</div>
      <Table
        columns={[
          { key: 'name', label: 'Budget', render: (r) => <button className="text-white underline-offset-2 hover:underline" onClick={() => onOpenBudget(r)} data-testid={`budget-open-${r.code}`}>{r.name}</button> },
          { key: 'kind', label: 'Kind', render: (r) => <Pill tone={r.kind === 'execution' ? 'green' : 'blue'}>{r.kind}</Pill> },
          { key: 'status', label: 'Lock', render: (r) => <Pill tone={r.status === 'unlocked' ? 'green' : 'red'}>{r.status || 'locked'}</Pill> },
          { key: 'allocated', label: 'Allocated', align: 'right', render: (r) => fmtMoney(r.allocated) },
          { key: 'received', label: 'Received', align: 'right', render: (r) => <span className="text-emerald-300">{fmtMoney(r.received)}</span> },
          { key: 'spent', label: 'Spent', align: 'right', render: (r) => <span className="text-rose-300">{fmtMoney(r.spent)}</span> },
          { key: 'available', label: 'Available', align: 'right', render: (r) => <span className="text-emerald-300 font-medium">{fmtMoney(r.available)}</span> },
        ]}
        rows={detail.budgets || []} empty="No budgets created yet for this project."
      />

      <div className="flex justify-between items-center mt-6 mb-2">
        <span className="text-[10px] tracking-[0.2em] uppercase text-white/45">SO Allocations · Value Accumulation</span>
        <Btn variant="accent" onClick={() => setOpenAlloc(true)} data-testid="proj-link-so">+ Link SO</Btn>
      </div>
      <Table
        columns={[
          { key: 'so_no', label: 'SO #', render: (r) => <span className="font-mono text-xs text-white/80">{r.so_no}</span> },
          { key: 'so_value', label: 'SO Value', align: 'right', render: (r) => fmtMoney(r.so_value) },
          { key: 'exec_pct', label: 'Exec %', align: 'right', render: (r) => `${r.exec_pct}%` },
          { key: 'exec_amount', label: 'Exec Amt', align: 'right', render: (r) => fmtMoney(r.exec_amount) },
          { key: 'parking_amount', label: 'Overhead Amt', align: 'right', render: (r) => fmtMoney(r.parking_amount) },
          { key: 'overhead_budget_name', label: 'Overhead → ', render: (r) => <span className="text-xs text-white/55">{r.overhead_budget_name || r.parking_budget_name}</span> },
          { key: 'received_pct', label: 'Received', align: 'right', render: (r) => <span className={r.gate_passed ? 'text-emerald-300' : 'text-orange-300'}>{r.received_pct}%</span> },
          { key: 'gate_passed', label: 'Gate', render: (r) => <Pill tone={r.gate_passed ? 'green' : 'orange'}>{r.gate_passed ? 'Unlocked' : `Locked · ${r.gate_pct}%`}</Pill> },
        ]}
        rows={detail.allocations || []} empty="No SOs linked yet."
        testidPrefix="alloc"
      />

      <AllocationForm open={openAlloc} onClose={() => setOpenAlloc(false)} project={detail} onSaved={onChange} />
    </Drawer>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ALLOCATION FORM (SO ↔ Project ↔ Budgets)
// ────────────────────────────────────────────────────────────────────────────
function AllocationForm({ open, onClose, project, onSaved }) {
  const [sos, setSos] = useState([]);
  const [execBudgets, setExecBudgets] = useState([]);
  const [overheads, setOverheads] = useState([]);
  const [form, setForm] = useState({ exec_pct: 70, overhead_pct: 30, gate_pct: project?.gate_pct || 40 });

  useEffect(() => {
    if (!open || !project) return;
    (async () => {
      const all = await fb.get('/sales-orders');
      const linked = (project.allocations || []).map(a => a.so_id);
      setSos(all.filter(s => !linked.includes(s.id) && s.status !== 'void'));
      const projBuds = await fb.get('/budgets', { project_id: project.id, kind: 'execution' });
      setExecBudgets(projBuds);
      const oh = await fb.get('/budgets', { standalone: true, kind: 'overhead' });
      setOverheads(oh);
      setForm({ exec_pct: 70, overhead_pct: 30, gate_pct: project.gate_pct || 40 });
    })();
  }, [open, project]);

  const updateExec = (v) => setForm({ ...form, exec_pct: Number(v), overhead_pct: Math.max(0, 100 - Number(v)) });
  const save = async () => {
    try {
      if (!form.so_id || !form.exec_budget_id || !form.overhead_budget_id) return toast.error('Pick SO + Execution + Overhead budgets');
      if (Math.round(Number(form.exec_pct) + Number(form.overhead_pct)) !== 100) return toast.error('Splits must sum to 100');
      await fb.post('/so-allocations', {
        so_id: form.so_id, project_id: project.id,
        exec_budget_id: form.exec_budget_id, overhead_budget_id: form.overhead_budget_id,
        exec_pct: Number(form.exec_pct), overhead_pct: Number(form.overhead_pct),
        gate_pct: Number(form.gate_pct),
      });
      toast.success('SO linked · Value Accumulation in progress'); onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const selectedSo = sos.find(s => s.id === form.so_id);
  const so_value = selectedSo ? Number(selectedSo.grand_total) : 0;

  return (
    <Drawer open={open} onClose={onClose} title="Link SO to Project" width="max-w-2xl">
      <div className="space-y-3">
        <Select label="Sales Order *" value={form.so_id || ''} onChange={(e) => setForm({ ...form, so_id: e.target.value })} data-testid="alloc-so">
          <option value="">— Select unlinked SO —</option>
          {sos.map((s) => <option key={s.id} value={s.id}>{s.doc_no} · {s.customer_name} · {fmtMoney(s.grand_total)}</option>)}
        </Select>
        {selectedSo && (
          <div className={`${SOFT} rounded-lg p-4`}>
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/45">SO Value</div>
            <div className="text-2xl text-white font-semibold mt-1">{fmtMoney(so_value)}</div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Select label="Execution Budget *" value={form.exec_budget_id || ''} onChange={(e) => setForm({ ...form, exec_budget_id: e.target.value })} data-testid="alloc-exec-budget">
            <option value="">— Reusable budget in this project —</option>
            {execBudgets.map((b) => <option key={b.id} value={b.id}>{b.name} · alloc {fmtMoney(b.allocated)}</option>)}
          </Select>
          <Select label="Overhead Pool *" value={form.overhead_budget_id || ''} onChange={(e) => setForm({ ...form, overhead_budget_id: e.target.value })} data-testid="alloc-overhead-budget">
            <option value="">— Pick existing global overhead pool —</option>
            {overheads.map((b) => <option key={b.id} value={b.id}>{b.name} · pool {fmtMoney(b.allocated)}</option>)}
          </Select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input label="Execution %" type="number" min="0" max="100" value={form.exec_pct} onChange={(e) => updateExec(e.target.value)} data-testid="alloc-exec-pct" />
          <Input label="Overhead %" type="number" value={form.overhead_pct} readOnly />
          <Input label="Gate %" type="number" value={form.gate_pct} onChange={(e) => setForm({ ...form, gate_pct: e.target.value })} />
        </div>
        {so_value > 0 && (
          <div className={`${SOFT} rounded-lg p-3 text-sm`}>
            <div className="flex justify-between"><span className="text-white/70">Execution will get</span><span className="text-emerald-300 tabular-nums">{fmtMoney(so_value * form.exec_pct / 100)}</span></div>
            <div className="flex justify-between"><span className="text-white/70">Overhead pool will get</span><span className="text-orange-300 tabular-nums">{fmtMoney(so_value * form.overhead_pct / 100)}</span></div>
            <div className="text-[10px] text-white/40 italic mt-2">Both budgets stay LOCKED until {form.gate_pct}% of the SO is collected via customer receipts.</div>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="accent" onClick={save} data-testid="alloc-save">Link & Allocate</Btn>
      </div>
    </Drawer>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// BUDGETS LIST (standalone screen)
// ────────────────────────────────────────────────────────────────────────────
export function Budgets() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [bid, setBid] = useState(null);
  const [form, setForm] = useState({ kind: 'overhead' });

  const reload = async () => {
    setRows(await fb.get('/budgets'));
    setProjects(await fb.get('/projects'));
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      if (!form.name) return toast.error('Name required');
      const payload = { ...form };
      if (payload.kind === 'overhead') payload.project_id = null;
      if (payload.kind === 'execution' && !payload.project_id) return toast.error('Execution budget needs a project');
      await fb.post('/budgets', payload);
      toast.success('Budget created'); setOpen(false); setForm({ kind: 'overhead' }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const filtered = filter === 'all' ? rows : rows.filter(b => b.kind === filter);

  return (
    <div data-testid="fb-budgets">
      <PageHeader title="Budgets" subtitle={`${rows.length} budgets · Execution (project-bound, reusable) + Overhead (global pool)`}
        right={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="fb-new-budget">+ New Budget</Btn>} />

      <div className="flex gap-2 mb-4">
        {[
          { k: 'all', l: 'All' },
          { k: 'execution', l: 'Execution' },
          { k: 'overhead', l: 'Overhead Pools' },
        ].map((t) => (
          <button key={t.k} onClick={() => setFilter(t.k)}
            className={`px-3 py-1.5 rounded text-xs tracking-[0.15em] uppercase ${filter === t.k ? 'bg-white text-neutral-900' : 'text-white/55 hover:text-white border border-white/10'}`}>
            {t.l}
          </button>
        ))}
      </div>

      <Table
        columns={[
          { key: 'code', label: 'Code', render: (r) => <span className="font-mono text-xs text-white/55">{r.code}</span> },
          { key: 'name', label: 'Name', render: (r) => <button className="text-white underline-offset-2 hover:underline" onClick={() => setBid(r.id)} data-testid={`budget-open-${r.code}`}>{r.name}</button> },
          { key: 'project_name', label: 'Project', render: (r) => r.project_name || <span className="text-white/30">— Global Pool —</span> },
          { key: 'kind', label: 'Kind', render: (r) => <Pill tone={r.kind === 'execution' ? 'green' : 'blue'}>{r.kind}</Pill> },
          { key: 'status', label: 'Gate', render: (r) => <Pill tone={r.status === 'unlocked' ? 'green' : 'red'}>{r.status || 'locked'}</Pill> },
          { key: 'allocated', label: 'Allocated', align: 'right', render: (r) => fmtMoney(r.allocated) },
          { key: 'received', label: 'Received', align: 'right', render: (r) => <span className="text-emerald-300">{fmtMoney(r.received)}</span> },
          { key: 'spent', label: 'Spent', align: 'right', render: (r) => <span className="text-rose-300">{fmtMoney(r.spent)}</span> },
          { key: 'available', label: 'Available', align: 'right', render: (r) => <span className="text-emerald-300 font-medium">{fmtMoney(r.available)}</span> },
        ]}
        rows={filtered} empty="No budgets in this filter."
        testidPrefix="budget"
      />

      <Drawer open={open} onClose={() => setOpen(false)} title="New Budget" width="max-w-lg">
        <div className="space-y-3">
          <Input label="Name *" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bud-name" />
          <Select label="Kind *" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, project_id: e.target.value === 'overhead' ? '' : form.project_id })}>
            <option value="overhead">Overhead (global pool · standalone)</option>
            <option value="execution">Execution (must belong to a project · reusable)</option>
          </Select>
          {form.kind !== 'overhead' && (
            <Select label="Project *" value={form.project_id || ''} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
              <option value="">—</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          )}
          <Textarea label="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={save} data-testid="bud-save">Save Budget</Btn>
        </div>
      </Drawer>

      <BudgetDetailDrawer bid={bid} onClose={() => setBid(null)} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// BUDGET DETAIL (Pro view — kept open with every detail)
// ────────────────────────────────────────────────────────────────────────────
function BudgetDetailDrawer({ bid, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (!bid) { setData(null); return; }
    (async () => setData(await fb.get(`/budgets/${bid}/detail`)))();
  }, [bid]);

  if (!bid) return null;
  if (!data) return <Drawer open onClose={onClose} title="Loading…" />;
  const { budget, totals, allocations, sales_orders, invoices, receipts, purchase_orders, bills, vendor_payments } = data;

  const tabs = [
    { k: 'overview', l: 'Overview' },
    { k: 'allocations', l: `Allocations · ${allocations.length}` },
    { k: 'sales', l: `Sales · ${sales_orders.length + invoices.length + receipts.length}` },
    { k: 'purchases', l: `Purchases · ${purchase_orders.length + bills.length + vendor_payments.length}` },
  ];

  return (
    <Drawer open onClose={onClose} title={`Budget · ${budget.name}`} width="max-w-6xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/45">{budget.kind}</div>
          <div className="text-white text-lg font-semibold">{budget.name}</div>
          <div className="text-xs text-white/45 mt-1">
            {budget.project_name ? `Project: ${budget.project_name}` : 'Global Overhead Pool'} · Code {budget.code}
          </div>
        </div>
        <Pill tone={budget.status === 'unlocked' ? 'green' : 'red'}>
          {budget.status === 'unlocked' ? 'Gate Unlocked' : 'Gate Locked'}
        </Pill>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-4" data-testid="budget-kpis">
        <Kpi label="Allocated" value={fmtMoney(totals.allocated)} sub={`Unlocked ${fmtMoney(totals.unlocked)} · Locked ${fmtMoney(totals.locked)}`} />
        <Kpi label="Receivable" value={fmtMoney(totals.receivable_outstanding)} sub={`Invoiced ${fmtMoney(totals.receivable_invoiced)}`} tone="orange" />
        <Kpi label="Received" value={fmtMoney(totals.received)} tone="green" />
        <Kpi label="Available" value={fmtMoney(totals.available)} sub="Unlocked − Committed − Spent" tone="green" />
        <Kpi label="Payable" value={fmtMoney(totals.payable_outstanding)} sub={`Billed ${fmtMoney(totals.payable_billed)}`} tone="orange" />
        <Kpi label="Paid" value={fmtMoney(totals.paid)} tone="red" />
        <Kpi label="Committed" value={fmtMoney(totals.committed)} sub="Open POs + unpaid bills" />
        <Kpi label="Margin" value={`${fmtMoney(totals.margin)} (${totals.margin_pct}%)`} tone={totals.margin >= 0 ? 'green' : 'red'} />
      </div>

      <div className="flex gap-2 mb-3 border-b border-white/10">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-3 py-2 text-xs tracking-[0.2em] uppercase ${tab === t.k ? 'text-white border-b-2 border-orange-400' : 'text-white/45 hover:text-white'}`}
            data-testid={`budget-tab-${t.k}`}>{t.l}</button>
        ))}
      </div>

      {tab === 'overview' && <BudgetOverview data={data} />}
      {tab === 'allocations' && <BudgetAllocs rows={allocations} />}
      {tab === 'sales' && <BudgetSales sos={sales_orders} invs={invoices} rcpts={receipts} />}
      {tab === 'purchases' && <BudgetPurchases pos={purchase_orders} bills={bills} vps={vendor_payments} />}
    </Drawer>
  );
}

function BudgetOverview({ data }) {
  const { totals } = data;
  const sections = [
    ['SO Allocations', data.allocations.length],
    ['Sales Orders', data.sales_orders.length],
    ['Invoices', data.invoices.length],
    ['Customer Receipts', data.receipts.length],
    ['Purchase Orders', data.purchase_orders.length],
    ['Bills', data.bills.length],
    ['Vendor Payments', data.vendor_payments.length],
  ];
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className={`${SOFT} rounded-xl p-5`}>
        <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-3">Transaction Count</div>
        <div className="space-y-2">
          {sections.map(([l, n]) => (
            <div key={l} className="flex justify-between text-sm"><span className="text-white/70">{l}</span><span className="text-white tabular-nums">{n}</span></div>
          ))}
        </div>
      </div>
      <div className={`${SOFT} rounded-xl p-5`}>
        <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-3">Mini P&L</div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-white/70">Revenue (received)</span><span className="text-emerald-300 tabular-nums">{fmtMoney(totals.revenue)}</span></div>
          <div className="flex justify-between"><span className="text-white/70">Cost (vendor paid)</span><span className="text-rose-300 tabular-nums">{fmtMoney(totals.cost)}</span></div>
          <div className="flex justify-between border-t border-white/10 pt-2 mt-2"><span className="text-white">Margin</span><span className={`tabular-nums font-medium ${totals.margin >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{fmtMoney(totals.margin)} ({totals.margin_pct}%)</span></div>
        </div>
      </div>
    </div>
  );
}

function BudgetAllocs({ rows }) {
  return (
    <Table
      columns={[
        { key: 'so_no', label: 'SO #', render: (r) => <span className="font-mono text-xs text-white/80">{r.so_no}</span> },
        { key: 'role', label: 'Role', render: (r) => <Pill tone={r.role === 'execution' ? 'green' : 'blue'}>{r.role}</Pill> },
        { key: 'amount', label: 'Amount', align: 'right', render: (r) => fmtMoney(r.amount) },
        { key: 'received_pct', label: 'Received', align: 'right', render: (r) => <span className={r.gate_passed ? 'text-emerald-300' : 'text-orange-300'}>{r.received_pct}%</span> },
        { key: 'gate_passed', label: 'Gate', render: (r) => <Pill tone={r.gate_passed ? 'green' : 'orange'}>{r.gate_passed ? 'Unlocked' : 'Locked'}</Pill> },
      ]}
      rows={rows} empty="No allocations on this budget yet."
    />
  );
}

function BudgetSales({ sos, invs, rcpts }) {
  return (
    <div className="space-y-5">
      <Section title={`Sales Orders · ${sos.length}`} rows={sos}
        cols={[['doc_no', 'SO #', 'mono'], ['customer_name', 'Customer'], ['date', 'Date', 'date'], ['grand_total', 'Total', 'money']]} />
      <Section title={`Invoices · ${invs.length}`} rows={invs}
        cols={[['doc_no', 'Inv #', 'mono'], ['customer_name', 'Customer'], ['date', 'Date', 'date'], ['grand_total', 'Total', 'money'], ['amount_paid', 'Paid', 'money'], ['balance_due', 'Due', 'money']]} />
      <Section title={`Customer Receipts · ${rcpts.length}`} rows={rcpts}
        cols={[['pay_no', 'Rct #', 'mono'], ['customer_name', 'Customer'], ['date', 'Date', 'date'], ['amount', 'Amount', 'money'], ['mode', 'Mode']]} />
    </div>
  );
}

function BudgetPurchases({ pos, bills, vps }) {
  return (
    <div className="space-y-5">
      <Section title={`Purchase Orders · ${pos.length}`} rows={pos}
        cols={[['doc_no', 'PO #', 'mono'], ['vendor_name', 'Vendor'], ['date', 'Date', 'date'], ['grand_total', 'Total', 'money']]} />
      <Section title={`Bills · ${bills.length}`} rows={bills}
        cols={[['doc_no', 'Bill #', 'mono'], ['vendor_name', 'Vendor'], ['date', 'Date', 'date'], ['grand_total', 'Total', 'money'], ['amount_paid', 'Paid', 'money'], ['balance_due', 'Due', 'money']]} />
      <Section title={`Vendor Payments · ${vps.length}`} rows={vps}
        cols={[['pay_no', 'Pay #', 'mono'], ['vendor_name', 'Vendor'], ['date', 'Date', 'date'], ['amount', 'Amount', 'money'], ['mode', 'Mode']]} />
    </div>
  );
}

function Section({ title, rows, cols }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-2">{title}</div>
      <Table
        columns={cols.map(([key, label, type]) => ({
          key, label,
          align: type === 'money' ? 'right' : undefined,
          render: (r) => {
            const v = r[key];
            if (type === 'mono') return <span className="font-mono text-xs text-white/80">{v}</span>;
            if (type === 'date') return fmtDate(v);
            if (type === 'money') return <span className="tabular-nums">{fmtMoney(v)}</span>;
            return v;
          },
        }))}
        rows={rows} empty="No records."
      />
    </div>
  );
}
